// Tausch: Firebase-ID-Token (post-2FA) → Backend-JWT mit adminAuth-Claim.
// Nach erfolgreichem Login wird die Session in den authStore geschrieben.

import { api, ApiError } from '../../lib/api';
import { useAuthStore, type AdminUser } from './authStore';

// Backend liefert für Admin-Login ohne 2FA-Faktor einen 403 mit
// "Admin login requires multi-factor authentication"-Message zurück.
// Frontend erkennt diesen Spezialfall, um den User zur MFA-Enrollment-
// Seite zu routen.
export function isAdminMfaMissingError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.status !== 403) return false;
  return /multi-factor/i.test(err.message);
}

interface FirebaseLoginResponse {
  user: AdminUser;
  accessToken: string;
  refreshToken: string;
}

export async function exchangeFirebaseTokenForAdminSession(
  firebaseIdToken: string,
): Promise<AdminUser> {
  try {
    const data = await api.post<FirebaseLoginResponse>('/admin/auth/firebase-login', {
      firebaseToken: firebaseIdToken,
    });

    useAuthStore.getState().setSession({
      user: data.user,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
    });

    return data.user;
  } catch (err) {
    if (err instanceof ApiError && err.status === 403) {
      // Kann sein: 2FA fehlt oder User hat keine ADMIN-Role. Backend gibt
      // "Account not found" für Letzteres zurück (Information-Leak-Schutz).
      throw err;
    }
    throw err;
  }
}

// Dev-Only Smoketest-Login. Backend-Endpoint ist nur bei
// ENABLE_DEV_AUTH_BYPASS=true verfuegbar — sonst 403. Wird in Production-Builds
// nicht aufgerufen (siehe LoginPage.tsx → import.meta.env.DEV).
export async function devLoginAsAdmin(): Promise<AdminUser> {
  const data = await api.post<FirebaseLoginResponse>('/admin/auth/dev-login');
  useAuthStore.getState().setSession({
    user: data.user,
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
  });
  return data.user;
}

export async function logoutFromAdmin(): Promise<void> {
  // Server-seitig alle Tokens revoken (logout-all). Nicht-blockierend bei Fehler —
  // wir wollen den Client immer ausloggen, auch wenn der Server gerade weg ist.
  try {
    await api.post('/admin/auth/logout-all');
  } catch {
    // ignoriert
  }
  useAuthStore.getState().clearSession();
}
